import type { GridApiPremium, GridColDef, GridValidRowModel } from '@mui/x-data-grid-premium';
import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AUTOSIZE_MEASUREMENT_CLASSNAME } from './CellContainer';

/** Headers are excluded on purpose; outliers are kept so one long value still widens a column. */
export const AUTOSIZE_OPTIONS = { includeHeaders: false, includeOutliers: true };

/** On the grid root until the first autosize pass finishes. */
export const AUTOSIZE_PENDING_CLASSNAME = 'data-grid-autosize-pending';

/**
 * Applied by DataGrid when `autoSizeColumns` is true (composed into `containerSx`).
 * Hides default-width columns until the first autosize pass and restores the theme-hidden
 * header filler so the header border spans the grid after columns shrink.
 *
 * Only header fillers are restored: body/scroller fillers stay theme-hidden. Showing them
 * paints MUI's dark-mode lightened pinned background as gray blocks beside/under the grid.
 * Header filler bg is transparent so the themed header row color shows through.
 */
export const AUTOSIZED_DATA_GRID_SX = {
    [`&.${AUTOSIZE_PENDING_CLASSNAME} :is(.MuiDataGrid-columnHeaders, .MuiDataGrid-virtualScrollerContent)`]: {
        visibility: 'hidden'
    },
    '& .MuiDataGrid-columnHeaders .MuiDataGrid-filler': {
        display: 'block',
        backgroundColor: 'transparent'
    }
};

type UseAutosizeColumnsParams = {
    apiRef: RefObject<GridApiPremium>;
    enabled: boolean;
    /** Rows are loaded and painted, so cells can be measured. */
    hasRenderedRows: boolean;
    /** Finished loading with zero rows — nothing to measure. */
    isEmptyAndSettled: boolean;
    /**
     * When true, pin the `actions` column on the right only if columns overflow the viewport.
     * Otherwise leave it after the last data column so wide screens do not show a large gap.
     */
    syncActionsPinning?: boolean;
};

export const ACTIONS_COLUMN_FIELD = 'actions';

/**
 * Pins `actions` to the right only when visible columns overflow the viewport.
 * When they fit, unpins so actions sit flush after the last data column.
 */
export function syncActionsColumnPinning(apiRef: RefObject<GridApiPremium>) {
    const api = apiRef.current;
    if (!api?.getVisibleColumns || !api.setPinnedColumns || !api.getPinnedColumns) {
        return;
    }

    const visibleColumns = api.getVisibleColumns();
    const hasActions = visibleColumns.some((column) => column.field === ACTIONS_COLUMN_FIELD);
    if (!hasActions) {
        return;
    }

    const dimensions = api.getRootDimensions?.();
    const viewportWidth = dimensions?.viewportOuterSize?.width;
    if (!viewportWidth) {
        return;
    }

    const availableWidth = viewportWidth - (dimensions.hasScrollY ? dimensions.scrollbarSize : 0);
    const columnsTotalWidth = visibleColumns.reduce((sum, column) => sum + (column.computedWidth || 0), 0);
    const shouldPin = columnsTotalWidth > availableWidth;

    const pinned = api.getPinnedColumns();
    const rightWithoutActions = (pinned.right ?? []).filter((field) => field !== ACTIONS_COLUMN_FIELD);
    const isPinned = (pinned.right ?? []).includes(ACTIONS_COLUMN_FIELD);

    if (shouldPin === isPinned) {
        return;
    }

    api.setPinnedColumns({
        ...pinned,
        right: shouldPin ? [...rightWithoutActions, ACTIONS_COLUMN_FIELD] : rightWithoutActions
    });
}

/** Actions / checkbox / detail-toggle have fixed chrome. Grouping (`__row_group_by…`) stays eligible. */
export function isAutosizableColumn(column: Pick<GridColDef, 'field' | 'type' | 'resizable'>) {
    const isAutosizable = column.type !== 'actions' && column.resizable !== false;

    return isAutosizable;
}

const AUTOSIZE_CONFLICTING_COLUMN_PROPS = ['flex', 'minWidth'] as const;

/** True when a cell value should not drive content-based autosize. */
export function isEmptyAutosizeCellValue(value: unknown): boolean {
    if (value == null) {
        return true;
    }
    if (typeof value === 'string') {
        return value.trim() === '';
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return false;
    }
    if (Array.isArray(value)) {
        return value.length === 0;
    }
    if (typeof value === 'object') {
        if ('label' in value) {
            return isEmptyAutosizeCellValue((value as { label: unknown }).label);
        }
        return Object.keys(value).length === 0;
    }
    return false;
}

/**
 * Whether any row has a non-empty value for this column (uses `valueGetter` when present).
 * Used when preparing column defs before the grid API is ready.
 *
 * MUI types `valueGetter`'s first arg as `never` unless column generics are fully specified,
 * so we assert a callable signature here to probe cell values outside the grid API.
 */
export function columnHasAutosizeContent(
    column: Pick<GridColDef, 'field' | 'valueGetter'>,
    rows: readonly GridValidRowModel[]
): boolean {
    return rows.some((row) => {
        const raw = row[column.field];
        if (!column.valueGetter) {
            return !isEmptyAutosizeCellValue(raw);
        }

        try {
            const valueGetter = column.valueGetter as (
                value: unknown,
                row: GridValidRowModel,
                column: GridColDef,
                apiRef: RefObject<GridApiPremium | null>
            ) => unknown;
            const value = valueGetter(raw, row, column as GridColDef, { current: null });
            return !isEmptyAutosizeCellValue(value);
        } catch {
            return !isEmptyAutosizeCellValue(raw);
        }
    });
}

function columnHasAutosizeContentFromApi(apiRef: RefObject<GridApiPremium>, field: string): boolean {
    return apiRef.current.getAllRowIds().some((id) => {
        const rowNode = apiRef.current.getRowNode(id);
        if (rowNode && rowNode.type !== 'leaf' && rowNode.type !== 'pinnedRow') {
            return false;
        }

        try {
            const params = apiRef.current.getCellParams(id, field);
            return !isEmptyAutosizeCellValue(params.formattedValue ?? params.value);
        } catch {
            return false;
        }
    });
}

/**
 * Drops `flex` / `minWidth` from column defs when the column has cell content to measure —
 * both fight content-based widths. Callers should still declare them as empty-column
 * fallbacks so headers stay readable when every cell in that column is empty.
 */
export function omitAutosizeConflictingColumnProps(column: GridColDef): GridColDef {
    const conflictingProps = AUTOSIZE_CONFLICTING_COLUMN_PROPS.filter((prop) => column[prop] != null);

    if (conflictingProps.length === 0) {
        return column;
    }

    const nextColumn: GridColDef = { ...column };
    for (const prop of conflictingProps) {
        delete nextColumn[prop];
    }

    return nextColumn;
}

/**
 * Prepares a column def for autosize:
 * - no rows → keep `flex` / `minWidth` (whole-grid empty fallback)
 * - column has content → drop `flex` / `minWidth` so autosize can measure cells
 * - column empty but rows exist → keep `minWidth` for the header, drop `flex` so it
 *   does not fight fixed-width siblings
 */
export function prepareColumnForAutosize(
    column: GridColDef,
    { enabled, hasRows, hasContent }: { enabled: boolean; hasRows: boolean; hasContent: boolean }
): GridColDef {
    if (!enabled || !hasRows) {
        return column;
    }

    if (hasContent) {
        return omitAutosizeConflictingColumnProps(column);
    }

    if (column.flex == null) {
        return column;
    }

    const nextColumn: GridColDef = { ...column };
    delete nextColumn.flex;
    return nextColumn;
}

/**
 * Sizes columns to their cell content once rows are ready, when groups expand, and when
 * column visibility changes (e.g. Summary → All Columns).
 *
 * Manually resized fields are locked out of later autosize passes. Remembered widths are
 * re-applied on `columnsChange` because callers that rebuild column definitions make MUI
 * reset every width to the value declared in the column def.
 *
 * Returns whether the first autosize pass is still pending (for `AUTOSIZE_PENDING_CLASSNAME`).
 */
export function useAutosizeColumns({
    apiRef,
    enabled,
    hasRenderedRows,
    isEmptyAndSettled,
    syncActionsPinning = false
}: UseAutosizeColumnsParams) {
    const widths = useRef<Record<string, number>>({});
    const lockedFields = useRef(new Set<string>());
    const hasAutosized = useRef(false);
    const muteApply = useRef(false);
    const [isPending, setIsPending] = useState(enabled && !isEmptyAndSettled);

    const hasRowsToMeasure = useCallback(() => apiRef.current.getAllRowIds().length > 0, [apiRef]);

    const syncActionsPinningIfNeeded = useCallback(() => {
        if (syncActionsPinning) {
            syncActionsColumnPinning(apiRef);
        }
    }, [apiRef, syncActionsPinning]);

    const snapshotWidths = useCallback(() => {
        apiRef.current.getAllColumns().forEach((column) => {
            if (isAutosizableColumn(column) && column.computedWidth > 0) {
                widths.current[column.field] = column.computedWidth;
            }
        });
    }, [apiRef]);

    const applyWidths = useCallback(
        (fields?: Set<string>) => {
            // Empty grids keep minWidth fallbacks — do not replay measured widths over them.
            if (muteApply.current || !hasRowsToMeasure()) {
                return;
            }

            muteApply.current = true;
            try {
                const entries = fields
                    ? [...fields].map((field) => [field, widths.current[field]] as const)
                    : Object.entries(widths.current);

                entries.forEach(([field, width]) => {
                    if (width == null) {
                        return;
                    }
                    const column = apiRef.current.getColumn(field);
                    if (column && isAutosizableColumn(column) && column.computedWidth !== width) {
                        apiRef.current.setColumnWidth(field, width);
                    }
                });
            } finally {
                muteApply.current = false;
            }

            syncActionsPinningIfNeeded();
        },
        [apiRef, hasRowsToMeasure, syncActionsPinningIfNeeded]
    );

    const autosizeColumns = useCallback(async () => {
        try {
            // Nothing to measure (e.g. empty grid after a view-preset visibility change).
            if (!hasRowsToMeasure()) {
                syncActionsPinningIfNeeded();
                return;
            }

            // Skip empty columns — they keep minWidth so headers stay readable.
            const columns = apiRef.current
                .getAllColumns()
                .filter(
                    (column) =>
                        isAutosizableColumn(column) &&
                        !lockedFields.current.has(column.field) &&
                        columnHasAutosizeContentFromApi(apiRef, column.field)
                )
                .map((column) => column.field);

            if (columns.length === 0) {
                syncActionsPinningIfNeeded();
                return;
            }

            muteApply.current = true;
            const root = apiRef.current.rootElementRef?.current;
            root?.classList.add(AUTOSIZE_MEASUREMENT_CLASSNAME);
            try {
                await apiRef.current.autosizeColumns({ ...AUTOSIZE_OPTIONS, columns });

                lockedFields.current.forEach((field) => {
                    const width = widths.current[field];
                    if (width == null) {
                        return;
                    }
                    const column = apiRef.current.getColumn(field);
                    if (column && column.computedWidth !== width) {
                        apiRef.current.setColumnWidth(field, width);
                    }
                });
                snapshotWidths();
            } finally {
                root?.classList.remove(AUTOSIZE_MEASUREMENT_CLASSNAME);
                muteApply.current = false;
            }

            syncActionsPinningIfNeeded();
        } catch (error) {
            console.error('Failed to autosize data grid columns', error);
            muteApply.current = false;
        }
    }, [apiRef, hasRowsToMeasure, snapshotWidths, syncActionsPinningIfNeeded]);

    const scheduleAutosize = useCallback(() => {
        if (!hasRowsToMeasure()) {
            syncActionsPinningIfNeeded();
            return;
        }
        window.setTimeout(() => void autosizeColumns(), 0);
    }, [autosizeColumns, hasRowsToMeasure, syncActionsPinningIfNeeded]);

    useEffect(() => {
        if (!enabled) {
            // Start fresh when autosize is turned back on (e.g. leaving manual column sizing).
            hasAutosized.current = false;
            lockedFields.current.clear();
            widths.current = {};
            setIsPending(false);
            return;
        }

        if (isEmptyAndSettled) {
            setIsPending(false);
            // Empty grids keep flex widths; still pack actions after the last column when they fit.
            const timeoutId = window.setTimeout(() => syncActionsPinningIfNeeded(), 0);
            return () => window.clearTimeout(timeoutId);
        }

        if (!hasRenderedRows || hasAutosized.current) {
            if (!hasAutosized.current) {
                setIsPending(true);
            }
            return;
        }

        // An empty grid already cleared `isPending`; without this the completion
        // setIsPending(false) is a no-op and the loading overlay never re-renders away.
        setIsPending(true);

        // Wait one tick so rows are in the DOM before MUI measures them.
        let cancelled = false;
        const timeoutId = window.setTimeout(() => {
            void runInitialAutosize();
        }, 0);

        async function runInitialAutosize() {
            await autosizeColumns();
            // Ignore the result if this effect was cleaned up (Strict Mode remount / unmount).
            if (cancelled) {
                return;
            }
            hasAutosized.current = true;
            setIsPending(false);
        }

        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [autosizeColumns, enabled, hasRenderedRows, isEmptyAndSettled, syncActionsPinningIfNeeded]);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const unsubscribers = [
            apiRef.current.subscribeEvent('rowExpansionChange', scheduleAutosize),
            apiRef.current.subscribeEvent('columnVisibilityModelChange', scheduleAutosize),
            apiRef.current.subscribeEvent('columnResizeStart', ({ field }) => {
                muteApply.current = true;
                lockedFields.current.add(field);
            }),
            apiRef.current.subscribeEvent('columnResizeStop', () => {
                snapshotWidths();
                muteApply.current = false;
                syncActionsPinningIfNeeded();
            }),
            apiRef.current.subscribeEvent('columnsChange', () => applyWidths())
        ];

        return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
    }, [apiRef, applyWidths, enabled, scheduleAutosize, snapshotWidths, syncActionsPinningIfNeeded]);

    // Independent of `enabled` so manual column sizing still pins/unpins actions on overflow.
    useEffect(() => {
        if (!syncActionsPinning) {
            return;
        }

        const unsubscribeWidthChange = apiRef.current.subscribeEvent('columnWidthChange', syncActionsPinningIfNeeded);
        // Visibility presets can cross the overflow threshold without a width/root resize.
        const unsubscribeVisibilityChange = apiRef.current.subscribeEvent(
            'columnVisibilityModelChange',
            syncActionsPinningIfNeeded
        );
        const root = apiRef.current.rootElementRef?.current;
        if (!root || typeof ResizeObserver === 'undefined') {
            return () => {
                unsubscribeWidthChange();
                unsubscribeVisibilityChange();
            };
        }

        const resizeObserver = new ResizeObserver(() => {
            syncActionsPinningIfNeeded();
        });
        resizeObserver.observe(root);

        return () => {
            unsubscribeWidthChange();
            unsubscribeVisibilityChange();
            resizeObserver.disconnect();
        };
    }, [apiRef, syncActionsPinning, syncActionsPinningIfNeeded]);

    return enabled && !isEmptyAndSettled && (!hasAutosized.current || isPending);
}
